<template>
  <MTPage>
    <div class="max-w-4xl mx-auto space-y-6">

      <!-- Hero Section -->
      <div class="bg-[var(--mt-card-bg)] rounded-2xl shadow-sm border border-[var(--mt-border)] p-4">
        <div class="flex items-center justify-between gap-3 mb-2">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center" :style="{ background: 'var(--mt-accent-purple-muted, var(--mt-accent-blue-muted))' }">
              <MTIcon name="flask-conical" :size="22" :style="{ color: 'var(--mt-accent-purple, var(--mt-accent-blue))' }" />
            </div>
            <h1 class="text-xl sm:text-2xl font-extrabold text-[var(--mt-text)]">Skills Check</h1>
          </div>
          <MTButton variant="outline" size="sm" @click="goDashboard">Back</MTButton>
        </div>
        <p class="text-xs text-[var(--mt-text-muted)] font-medium">
          Quick check to understand your strengths and personalise your learning
          <template v-if="sessionStarted && !sessionCompleted">
            <span class="opacity-40 mx-1">&middot;</span>
            {{ currentIndex + 1 }}/{{ totalQuestions }} answered
          </template>
        </p>
      </div>

      <!-- BEFORE START / LOADING -->
      <MTCard v-if="!sessionStarted && !sessionCompleted" class="p-6 text-center">

        <!-- Feature not available on current plan -->
        <template v-if="!skillsCheckEnabled">
          <div class="flex justify-center mb-3">
            <div class="w-16 h-16 rounded-full flex items-center justify-center" :style="{ background: 'var(--mt-accent-blue-muted)' }">
              <MTIcon name="lock" :size="32" :style="{ color: 'var(--mt-accent-blue)' }" />
            </div>
          </div>
          <h2 class="text-lg font-semibold mb-2">Skills Check Requires an Upgrade</h2>
          <p class="text-[var(--mt-text-muted)] mb-2">
            The Skills Check feature runs a personalised diagnostic to find your strengths and gaps across the full curriculum.
          </p>
          <p class="text-[var(--mt-text-muted)] mb-4">
            Upgrade to <strong>Pro</strong>, <strong>Pro Plus</strong> or <strong>Family</strong> subscription to unlock unlimited skills checks and a tailored learning plan.
          </p>
          <div class="flex flex-col sm:flex-row gap-3 justify-center">
            <MTButton size="lg" variant="outline" @click="goDashboard">Back to Home</MTButton>
            <RouterLink to="/parent/subscription">
              <MTButton size="lg" variant="primary">Upgrade Plan →</MTButton>
            </RouterLink>
          </div>
        </template>

        <!-- Monthly limit reached -->
        <template v-else-if="skillsCheckLimited">
          <div class="flex justify-center mb-3">
            <div class="w-16 h-16 rounded-full flex items-center justify-center" :style="{ background: 'var(--mt-accent-blue-muted)' }">
              <MTIcon name="lock" :size="32" :style="{ color: 'var(--mt-accent-blue)' }" />
            </div>
          </div>
          <h2 class="text-lg font-semibold mb-2">Monthly Limit Reached</h2>
          <p class="text-[var(--mt-text-muted)] mb-2">
            You've completed {{ diagnosticsDoneThisMonth }} skills check{{ diagnosticsDoneThisMonth !== 1 ? 's' : '' }} this month
            (limit: {{ maxSkillsChecks }} on your current plan).
          </p>
          <p class="text-[var(--mt-text-muted)] mb-4">
            Upgrade to Pro or Family for unlimited skills checks.
          </p>
          <div class="flex flex-col sm:flex-row gap-3 justify-center">
            <MTButton size="lg" variant="outline" @click="goDashboard">Back to Home</MTButton>
            <RouterLink to="/parent/subscription">
              <MTButton size="lg" variant="primary">Upgrade Plan →</MTButton>
            </RouterLink>
          </div>
        </template>

        <!-- Normal start flow -->
        <template v-else-if="!loading">
          <div class="flex justify-center mb-3">
            <div class="w-16 h-16 rounded-full flex items-center justify-center" :style="{ background: 'var(--mt-icon-blue-bg)' }">
              <MTIcon name="flask-conical" :size="32" :style="{ color: 'var(--mt-icon-blue-fg)' }" />
            </div>
          </div>
          <h2 class="text-lg font-semibold mb-2">Ready to Begin?</h2>
          <p class="text-[var(--mt-text-muted)] mb-4">
            Answer questions across different topics so we can build your personalised learning plan.
          </p>
          <p v-if="startError" class="text-sm text-red-500 mb-3">{{ startError }}</p>
          <MTButton size="lg" variant="primary" @click="startSession">
            <MTIcon name="play-circle" :size="18" class="mr-2" />
            Start Skills Check
          </MTButton>
        </template>
        <template v-else>
          <div class="flex justify-center mb-4">
            <MTIcon name="loader" :size="32" class="animate-spin" :style="{ color: 'var(--mt-accent-blue)' }" />
          </div>
          <p class="text-lg font-semibold mb-1">Preparing your skills check...</p>
          <p class="text-sm text-[var(--mt-text-muted)]">Selecting questions based on your curriculum</p>
        </template>
      </MTCard>

      <!-- IN PROGRESS -->
      <MTCard v-else-if="sessionStarted && !sessionCompleted" class="p-6">
        <!-- Progress row: answered count left, topic pill right -->
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-[var(--mt-text-muted)]">
            {{ currentIndex + 1 }} / {{ totalQuestions }} answered
          </span>
          <span
            v-if="currentTopicLabel"
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[var(--mt-accent-blue-muted)] text-[var(--mt-accent-blue)]"
          >
            <MTIcon name="layers" :size="12" />
            {{ currentTopicLabel }}
          </span>
        </div>

        <!-- Confidence meter -->
        <ConfidenceMeter :confidence="confidence" class="mb-4" />

        <!-- Question -->
        <QuestionBlock
          v-if="currentQuestion"
          :question="currentQuestion.question"
          @questionCompleted="submitQuestionResult"
        />

        <!-- Stop button -->
        <div class="flex justify-center mt-5 pt-4 border-t border-[var(--mt-border)]">
          <button
            type="button"
            class="text-xs text-[var(--mt-text-muted)] hover:text-red-500 transition-colors font-medium"
            @click="handleStop"
          >
            Stop &amp; save progress
          </button>
        </div>
      </MTCard>

      <!-- COMPLETED (natural) -->
      <MTCard v-else-if="sessionCompleted && !wasStopped" class="p-6 text-center">
        <div class="flex justify-center mb-4">
          <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <MTIcon name="party-popper" :size="32" class="text-green-600" />
          </div>
        </div>
        <h2 class="text-xl font-semibold mb-3">Skills Check Complete</h2>
        <p class="text-[var(--mt-text-muted)] mb-4">
          We've updated your learning plan based on your answers.
        </p>
        <MTButton variant="primary" size="md" @click="goDashboard">
          Go to Dashboard
        </MTButton>
      </MTCard>

      <!-- STOPPED EARLY -->
      <MTCard v-else class="p-6 text-center">
        <div class="flex justify-center mb-4">
          <div class="w-16 h-16 rounded-full flex items-center justify-center" :style="{ background: 'var(--mt-accent-blue-muted)' }">
            <MTIcon name="bookmark-check" :size="32" :style="{ color: 'var(--mt-accent-blue)' }" />
          </div>
        </div>
        <h2 class="text-xl font-semibold mb-3">Progress Saved</h2>
        <p class="text-[var(--mt-text-muted)] mb-4">
          We've saved your answers from the {{ currentIndex }} question{{ currentIndex === 1 ? '' : 's' }} you completed.
          You can finish the full skills check any time from the dashboard.
        </p>
        <MTButton variant="primary" size="md" @click="goDashboard">
          Go to Dashboard
        </MTButton>
      </MTCard>

    </div>
  </MTPage>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { onBeforeRouteLeave } from "vue-router";
import { useAnalytics } from "@/composables/useAnalytics";

import MTPage from "@/components/ui/MTPage.vue";
import MTCard from "@/components/ui/MTCard.vue";
import MTButton from "@/components/ui/MTButton.vue";
import MTIcon from "@/components/ui/MTIcon.vue";
import ConfidenceMeter from "@/components/ui/ConfidenceMeter.vue";
import QuestionBlock from "@/components/questions/QuestionBlock.vue";

// Session logic
import { useDiagnosticSession } from "@/composables/useDiagnosticSession";
import { usePlanLimits } from "@/composables/usePlanLimits";

const router = useRouter();
const route = useRoute();

const childId = route.params.studentId;
const session = useDiagnosticSession(childId);
const { track } = useAnalytics();
const planLimits = usePlanLimits();

const {
  sessionStarted,
  sessionCompleted,
  currentQuestion,
  currentIndex,
  totalQuestions,
  confidence,
  currentTopicLabel,
  start,
  submitQuestionResult,
  stopSession,
} = session;

const loading = ref(false);
const startError = ref('');
const wasStopped = ref(false);

// Feature + quota checks
const skillsCheckEnabled = computed(() => planLimits.isFeatureEnabled('skillsCheck'));
const skillsCheckLimited = computed(() =>
  skillsCheckEnabled.value && !planLimits.canStartSkillsCheck(childId)
);
const diagnosticsDoneThisMonth = computed(() => planLimits.diagnosticsThisMonth(childId));
const maxSkillsChecks = computed(() => planLimits.limits.value.maxSkillsChecksPerMonth);

onMounted(() => planLimits.loadPlans());

async function startSession() {
  if (!skillsCheckEnabled.value) {
    startError.value = 'Skills Check is not available on your current plan. Please upgrade to access this feature.';
    return;
  }
  if (skillsCheckLimited.value) {
    startError.value = `You've used your ${maxSkillsChecks.value} skills check${maxSkillsChecks.value !== 1 ? 's' : ''} this month. Upgrade your plan for unlimited checks.`;
    return;
  }
  loading.value = true;
  startError.value = '';
  await start();
  loading.value = false;
  if (!sessionStarted.value) {
    startError.value = 'No questions available. Please try again later.';
  } else {
    track('skills_check_started');
  }
}

function handleStop() {
  wasStopped.value = true;
  stopSession();
  track('skills_check_abandoned', {
    questions_answered: currentIndex.value,
    total_questions: totalQuestions.value,
    progress_percent: totalQuestions.value
      ? Math.round((currentIndex.value / totalQuestions.value) * 100)
      : 0,
  });
}

onBeforeRouteLeave(() => {
  // Implicit abandonment — navigated away while session was active
  if (sessionStarted.value && !sessionCompleted.value && !wasStopped.value) {
    track('skills_check_abandoned', {
      questions_answered: currentIndex.value,
      total_questions: totalQuestions.value,
      progress_percent: totalQuestions.value
        ? Math.round((currentIndex.value / totalQuestions.value) * 100)
        : 0,
    });
  }
});

function goDashboard() {
  router.push(`/student/${childId}/dashboard`);
}
</script>
